import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { todayLocalISO } from "@/lib/dates";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatINR } from "@/lib/format";
import {
  Plus, Check, X, ExternalLink, FileDown, FileArchive, Paperclip, Trash2,
  Search, ClipboardCheck, Wallet, AlertTriangle, Pencil,
} from "lucide-react";
import { getUser, roleDisplayLabel } from "@/lib/auth";

const CATEGORIES = ["BIB", "CO2", "Water Jar", "Cup", "Travel", "Miscellaneous", "Store Setup", "Fooding", "Lodging", "Conveyance", "Promoter Salary"];
const PAYMENT_MODES = ["Cash", "Card", "UPI"];
const CENTRAL_ID = "CENTRAL";
const CENTRAL_LABEL = "CENTRAL - Head Office";
const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-800 border border-amber-200",
  approved: "bg-green-100 text-green-800 border border-green-200",
  rejected: "bg-red-100 text-red-700 border border-red-200",
};

export default function AdminExpenses() {
  const me = getUser();
  const isSupOrAdmin = me?.role === "Admin" || me?.role === "Supervisor";

  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [stores, setStores] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState({
    store_id: "", zone: "", category: "", approval_status: "",
    from_date: "", to_date: "", q: "", scope: "all",   // scope: "all" | "stores" | "central"
  });

  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [editForm, setEditForm] = useState(null);   // FIX 12 — edit dialog
  const [editSaving, setEditSaving] = useState(false);

  const [confirmDel, setConfirmDel] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const buildParams = useCallback(() => {
    const p = {};
    if (filter.store_id) p.store_id = filter.store_id;
    if (filter.zone) p.zone = filter.zone;
    if (filter.category) p.category = filter.category;
    if (filter.approval_status) p.approval_status = filter.approval_status;
    if (filter.from_date) p.from_date = filter.from_date;
    if (filter.to_date) p.to_date = filter.to_date;
    // Scope filter takes precedence over an explicit store filter — user picked "Central only" or "Stores only".
    if (filter.scope === "central") p.store_id = CENTRAL_ID;
    return p;
  }, [filter.store_id, filter.zone, filter.category, filter.approval_status, filter.from_date, filter.to_date, filter.scope]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s, st, z] = await Promise.all([
        api.get("/expenses", { params: buildParams() }),
        api.get("/expenses/summary", { params: { from_date: filter.from_date, to_date: filter.to_date } }),
        api.get("/stores"),
        api.get("/zones"),
      ]);
      setExpenses(e.data); setSummary(s.data); setStores(st.data); setZones(z.data);
    } catch (err) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [buildParams, filter.from_date, filter.to_date]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setForm({
    store_id: stores[0]?.id || "", category: "Travel",
    amount: "", billable_to_client: false, notes: "",
    payment_mode: "",
    receipt_path: null, receipt_name: null,
    date: todayLocalISO(),
  });
  useEffect(() => { setOpenForm(!!form); }, [form]);

  const save = async () => {
    if (!form.store_id) return toast.error("Store required");
    if (!(Number(form.amount) > 0)) return toast.error("Amount must be > 0");
    if (!form.payment_mode) return toast.error("Select Mode of Payment");
    setSaving(true);
    try {
      const payload = {
        store_id: form.store_id, category: form.category,
        amount: Number(form.amount), billable_to_client: !!form.billable_to_client,
        payment_mode: form.payment_mode,
        notes: form.notes || "",
        receipt_path: form.receipt_path, receipt_name: form.receipt_name,
        date: form.date,
      };
      await api.post("/expenses", payload);
      toast.success("Expense submitted for approval");
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const openEdit = (e) => setEditForm({
    id: e.id,
    store_id: e.store_id, category: e.category,
    amount: String(e.amount ?? ""),
    billable_to_client: !!e.billable_to_client,
    notes: e.notes || "",
    payment_mode: e.payment_mode || "",
    receipt_path: e.receipt_path, receipt_name: e.receipt_name,
    date: e.date,
    approval_status: e.approval_status,
    created_by_id: e.created_by_id,
    created_by: e.created_by,
  });

  const saveEdit = async () => {
    if (!editForm) return;
    if (!editForm.store_id) return toast.error("Store required");
    if (!(Number(editForm.amount) > 0)) return toast.error("Amount must be > 0");
    setEditSaving(true);
    try {
      const payload = {
        store_id: editForm.store_id,
        category: editForm.category,
        amount: Number(editForm.amount),
        billable_to_client: !!editForm.billable_to_client,
        payment_mode: editForm.payment_mode,
        notes: editForm.notes || "",
        receipt_path: editForm.receipt_path,
        receipt_name: editForm.receipt_name,
        date: editForm.date,
      };
      await api.patch(`/expenses/${editForm.id}`, payload);
      toast.success("Expense updated");
      setEditForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setEditSaving(false); }
  };

  const uploadReceipt = async (file) => {
    if (!file || !form) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload-doc", fd);
      setForm(f => ({ ...f, receipt_path: data.path, receipt_name: data.original_name }));
      toast.success("Receipt attached");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const approve = async (exp) => {
    try { await api.patch(`/expenses/${exp.id}/approval`, { approval_status: "approved" }); toast.success("Approved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitReject = async () => {
    if (!rejectFor) return;
    try {
      await api.patch(`/expenses/${rejectFor.id}/approval`, { approval_status: "rejected", rejection_reason: rejectReason });
      toast.success("Rejected"); setRejectFor(null); setRejectReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async () => {
    if (!confirmDel) return;
    try { await api.delete(`/expenses/${confirmDel.id}`); toast.success("Deleted"); setConfirmDel(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openFile = (path) => {
    const token = localStorage.getItem("bb_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`, "_blank");
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get("/expenses/export.xlsx", { params: buildParams(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `expenses_${todayLocalISO()}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Excel export failed"); }
  };
  const exportZip = async () => {
    try {
      const r = await api.get("/expenses/receipts.zip", { params: buildParams(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `expense_receipts_${todayLocalISO()}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("ZIP export failed"); }
  };

  const filtered = useMemo(() => expenses.filter(e => {
    if (filter.scope === "stores" && e.store_id === CENTRAL_ID) return false;
    if (filter.scope === "central" && e.store_id !== CENTRAL_ID) return false;
    if (filter.q && !`${e.store_name} ${e.category} ${e.notes || ""} ${e.created_by || ""}`.toLowerCase().includes(filter.q.toLowerCase())) return false;
    return true;
  }), [expenses, filter.q, filter.scope]);

  const canEditExpense = useCallback((e) => {
    if (!me) return false;
    if (me.role === "Admin" || me.role === "SuperAdmin") return true;
    if (me.role === "Supervisor") {
      return e.created_by_id === me.id && (e.approval_status === "pending" || e.approval_status === "rejected");
    }
    return false;
  }, [me]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Expenses" subtitle="Central register · approvals · receipts" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">

          {/* KPIs */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Total spend" value={formatINR(summary.total_amount)} color="text-brand-red" icon={Wallet} />
              <KPI label="Approved" value={formatINR(summary.by_status.approved)} color="text-green-700" />
              <KPI label="Pending" value={formatINR(summary.by_status.pending)} sub={`${summary.pending_count} to review`} color="text-amber-700" icon={ClipboardCheck} />
              <KPI label="Rejected" value={formatINR(summary.by_status.rejected)} color="text-red-700" />
            </div>
          )}

          {/* Filters */}
          <Card className="rounded-xl border-gray-200 bg-white p-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px] max-w-sm">
                <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="exp-search" placeholder="Search store, notes, submitter…" value={filter.q}
                  onChange={e => setFilter({ ...filter, q: e.target.value })} className="pl-9" />
              </div>
              <select data-testid="exp-scope-filter" value={filter.scope}
                onChange={e => setFilter({ ...filter, scope: e.target.value, store_id: "" })}
                className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
                <option value="all">All (Stores + Central)</option>
                <option value="stores">Store-wise only</option>
                <option value="central">Central only</option>
              </select>
              <select data-testid="exp-zone-filter" value={filter.zone} onChange={e => setFilter({ ...filter, zone: e.target.value })}
                className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
                <option value="">All zones</option>{zones.map(z => <option key={z.id} value={z.name}>{z.name}</option>)}
              </select>
              <select data-testid="exp-cat-filter" value={filter.category} onChange={e => setFilter({ ...filter, category: e.target.value })}
                className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
                <option value="">All categories</option>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select data-testid="exp-status-filter" value={filter.approval_status} onChange={e => setFilter({ ...filter, approval_status: e.target.value })}
                className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
                <option value="">All statuses</option><option value="pending">Pending</option>
                <option value="approved">Approved</option><option value="rejected">Rejected</option>
              </select>
              <Input data-testid="exp-from-date" type="date" value={filter.from_date}
                onChange={e => setFilter({ ...filter, from_date: e.target.value })} className="w-40" />
              <Input data-testid="exp-to-date" type="date" value={filter.to_date}
                onChange={e => setFilter({ ...filter, to_date: e.target.value })} className="w-40" />
              <div className="ml-auto flex items-center gap-2">
                <Button data-testid="exp-export-xlsx" variant="outline" onClick={exportXlsx}>
                  <FileDown className="h-4 w-4 mr-2" /> Excel
                </Button>
                <Button data-testid="exp-export-zip" variant="outline" onClick={exportZip}>
                  <FileArchive className="h-4 w-4 mr-2" /> Receipts ZIP
                </Button>
                <Button data-testid="add-expense-btn" onClick={openNew} className="bg-brand-red hover:bg-[#B31721]">
                  <Plus className="h-4 w-4 mr-2" /> New Expense
                </Button>
              </div>
            </div>
          </Card>

          {/* Table */}
          <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="expenses-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Store · Zone</th>
                    <th className="text-left px-4 py-3">Category</th>
                    <th className="text-right px-4 py-3">Amount</th>
                    <th className="text-left px-4 py-3">Submitted by</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Receipt</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={8} className="text-center py-8 text-gray-500">Loading…</td></tr>}
                  {!loading && filtered.map(e => (
                    <tr key={e.id} className="border-t border-gray-100" data-testid={`exp-row-${e.id.slice(0,8)}`}>
                      <td className="px-4 py-3">{e.date}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{e.store_name}</div>
                        <div className="text-xs text-gray-500">{e.city} · {e.zone || "—"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100 border-0">{e.category}</Badge>
                        {e.billable_to_client && <div className="text-[10px] uppercase font-bold text-brand-red mt-1">Billable</div>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold">{formatINR(e.amount)}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm">{e.created_by}</div>
                        <div className="text-xs text-gray-500">{roleDisplayLabel(e.created_by_role)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${STATUS_BADGE[e.approval_status] || "bg-gray-100"} hover:${STATUS_BADGE[e.approval_status] || "bg-gray-100"}`}>
                          {e.approval_status}
                        </Badge>
                        {e.rejection_reason && <div className="text-[11px] text-red-700 mt-1 max-w-xs truncate">{e.rejection_reason}</div>}
                        {e.approved_by && (
                          <div className="text-[11px] text-gray-500 mt-0.5">{e.approval_status === "approved" ? "by " : "by "}{e.approved_by}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {(e.receipt_path || e.receipt_photo)
                          ? <Button size="sm" variant="outline" onClick={() => openFile(e.receipt_path || e.receipt_photo)} data-testid={`exp-view-receipt-${e.id.slice(0,8)}`}>
                              <ExternalLink className="h-3.5 w-3.5 mr-1" /> View
                            </Button>
                          : <span className="text-xs text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {isSupOrAdmin && e.approval_status === "pending" && (
                          // Supervisors cannot approve their OWN expense claim — must be escalated to Admin.
                          // Backend stores the submitter id in `created_by_id`.
                          me?.role === "Supervisor" && e.created_by_id && me?.id && e.created_by_id === me.id ? (
                            <span className="text-[11px] text-gray-500 italic" data-testid={`exp-self-note-${e.id.slice(0,8)}`}>
                              Your own expenses are approved by Admin
                            </span>
                          ) : (
                            <>
                              <Button size="sm" variant="outline" onClick={() => approve(e)}
                                className="mr-1 text-green-700 border-green-200 hover:bg-green-50"
                                data-testid={`exp-approve-${e.id.slice(0,8)}`}>
                                <Check className="h-3.5 w-3.5 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => { setRejectFor(e); setRejectReason(""); }}
                                className="mr-1 text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`exp-reject-${e.id.slice(0,8)}`}>
                                <X className="h-3.5 w-3.5 mr-1" /> Reject
                              </Button>
                            </>
                          )
                        )}
                        {canEditExpense(e) && (
                          <Button size="sm" variant="outline" onClick={() => openEdit(e)}
                            className="mr-1 text-gray-700 border-gray-300 hover:bg-gray-50"
                            data-testid={`exp-edit-${e.id.slice(0,8)}`}
                            title={me?.role === "Supervisor"
                              ? "Edit your own pending / rejected expense"
                              : "Edit expense"}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {me?.role === "Admin" && (
                          <Button size="sm" variant="outline" onClick={() => setConfirmDel(e)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`exp-delete-${e.id.slice(0,8)}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!loading && filtered.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-8 text-gray-500">No expenses match your filters</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </main>
      </div>

      {/* New Expense Dialog */}
      {form && (
        <Dialog open={openForm} onOpenChange={(v) => !v && setForm(null)}>
          <DialogContent data-testid="expense-form-dialog" className="max-w-lg rounded-2xl">
            <DialogHeader><DialogTitle>Log expense</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Store</label>
                {stores.length === 0 && me?.role === "Promoter" ? (
                  <div data-testid="exp-no-stores" className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                    No stores assigned to you. Ask your Admin to assign a store before adding an expense.
                  </div>
                ) : (
                  <select data-testid="exp-store-select" value={form.store_id} onChange={e => setForm({ ...form, store_id: e.target.value })}
                    className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                    {stores.map(s => <option key={s.id} value={s.id}>{s.store_name} · {s.city}</option>)}
                    {['Admin', 'SuperAdmin', 'Supervisor'].includes(me?.role) && <option value={CENTRAL_ID}>{CENTRAL_LABEL}</option>}
                  </select>
                )}
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Category</label>
                <select data-testid="exp-cat-select" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Amount (₹)</label>
                <Input data-testid="exp-amount-input" inputMode="decimal" value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value.replace(/[^\d.]/g, "") })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Date</label>
                <Input data-testid="exp-date-input" type="date" value={form.date}
                  onChange={e => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Billable to client?</label>
                <select data-testid="exp-billable-select" value={form.billable_to_client ? "yes" : "no"}
                  onChange={e => setForm({ ...form, billable_to_client: e.target.value === "yes" })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  <option value="no">No</option><option value="yes">Yes — bill CCI</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Mode of Payment</label>
                <select data-testid="exp-pay-select" value={form.payment_mode}
                  onChange={e => setForm({ ...form, payment_mode: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  <option value="">Select mode</option>
                  {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Notes</label>
                <Input data-testid="exp-notes-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Receipt (image or PDF)</label>
                <div className="flex items-center gap-2">
                  <Input data-testid="exp-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.docx"
                    onChange={e => uploadReceipt(e.target.files?.[0])} className="flex-1" />
                  {form.receipt_path && <Button size="sm" variant="outline" onClick={() => openFile(form.receipt_path)}><ExternalLink className="h-3.5 w-3.5 mr-1" /> View</Button>}
                </div>
                {form.receipt_name && <div className="text-xs text-gray-500 mt-1"><Paperclip className="h-3 w-3 inline mr-1" />{form.receipt_name}</div>}
              </div>
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" /> New expenses go into <b>Pending</b> — a Co-ordinator or Admin must approve.
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
              <Button data-testid="save-expense-btn" onClick={save}
                disabled={saving || !form.store_id}
                className="bg-brand-red hover:bg-[#B31721]">Submit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Expense Dialog — FIX 12: Supervisor can edit OWN pending/rejected; Admin can edit any status */}
      {editForm && (
        <Dialog open={!!editForm} onOpenChange={(v) => !v && setEditForm(null)}>
          <DialogContent data-testid="expense-edit-dialog" className="max-w-lg rounded-2xl" aria-describedby="exp-edit-desc">
            <DialogHeader>
              <DialogTitle>Edit expense</DialogTitle>
            </DialogHeader>
            <div id="exp-edit-desc" className="grid grid-cols-2 gap-3">
              <div className="col-span-2 text-[11px] text-gray-500">
                Submitted by <b>{editForm.created_by}</b>. Status:{" "}
                <Badge className={`${STATUS_BADGE[editForm.approval_status] || "bg-gray-100"} hover:${STATUS_BADGE[editForm.approval_status] || "bg-gray-100"}`}>
                  {editForm.approval_status}
                </Badge>
                {editForm.approval_status === "rejected" && me?.role === "Supervisor" && (
                  <div className="mt-1 text-amber-700">Saving will re-submit this expense (status → pending).</div>
                )}
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Store</label>
                <select data-testid="exp-edit-store-select" value={editForm.store_id}
                  onChange={e => setEditForm({ ...editForm, store_id: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  {stores.map(s => <option key={s.id} value={s.id}>{s.store_name} · {s.city}</option>)}
                  <option value={CENTRAL_ID}>{CENTRAL_LABEL}</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Category</label>
                <select data-testid="exp-edit-cat-select" value={editForm.category}
                  onChange={e => setEditForm({ ...editForm, category: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Amount (₹)</label>
                <Input data-testid="exp-edit-amount-input" inputMode="decimal" value={editForm.amount}
                  onChange={e => setEditForm({ ...editForm, amount: e.target.value.replace(/[^\d.]/g, "") })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Date</label>
                <Input data-testid="exp-edit-date-input" type="date" value={editForm.date}
                  onChange={e => setEditForm({ ...editForm, date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Billable to client?</label>
                <select data-testid="exp-edit-billable-select" value={editForm.billable_to_client ? "yes" : "no"}
                  onChange={e => setEditForm({ ...editForm, billable_to_client: e.target.value === "yes" })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  <option value="no">No</option><option value="yes">Yes — bill CCI</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Mode of Payment</label>
                <select data-testid="exp-edit-pay-select" value={editForm.payment_mode}
                  onChange={e => setEditForm({ ...editForm, payment_mode: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  <option value="">Select mode</option>
                  {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Notes</label>
                <Input data-testid="exp-edit-notes-input" value={editForm.notes}
                  onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
              </div>
              {editForm.receipt_name && (
                <div className="col-span-2 text-xs text-gray-500">
                  <Paperclip className="h-3 w-3 inline mr-1" />{editForm.receipt_name}
                  <button className="ml-2 underline" onClick={() => openFile(editForm.receipt_path)}>View</button>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditForm(null)}>Cancel</Button>
              <Button data-testid="save-expense-edit-btn" onClick={saveEdit}
                disabled={editSaving} className="bg-brand-red hover:bg-[#B31721]">
                Save changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Reject Dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent data-testid="reject-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>Reject expense</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="text-sm text-gray-700">
              Reject <b>{formatINR(rejectFor?.amount || 0)}</b> {rejectFor?.category} expense from <b>{rejectFor?.store_name}</b>?
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Reason (optional)</label>
              <Input data-testid="reject-reason-input" value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="e.g. Missing receipt" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button data-testid="confirm-reject-btn" onClick={submitReject} className="bg-red-600 hover:bg-red-700 text-white">
              <X className="h-4 w-4 mr-2" /> Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Delete expense?</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700">
            Delete <b>{formatINR(confirmDel?.amount || 0)}</b> {confirmDel?.category} expense from <b>{confirmDel?.store_name}</b>? This cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button data-testid="confirm-del-exp" onClick={del} className="bg-red-600 hover:bg-red-700 text-white"><Trash2 className="h-4 w-4 mr-2" /> Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, sub, color, icon: Icon }) {
  return (
    <Card className="rounded-xl border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase font-bold tracking-[0.14em] text-gray-500">{label}</div>
          <div className={`font-display text-2xl font-black mt-1 ${color}`}>{value}</div>
          {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
        </div>
        {Icon && <Icon className="h-5 w-5 text-gray-300" />}
      </div>
    </Card>
  );
}
